import axios, { AxiosError, AxiosRequestConfig } from 'axios'

import { msalInstance } from '@/modules/Auth'
import { ROUTES } from '@/router'
import { useAuthStore } from '@/store/auth-store'
import { clearLocalStorageItem } from '../localStorage'
import { isOffline, isUserError, shouldTrackRequest } from './utils'

const BASE_URL = import.meta.env.VITE_API_URL
const FALLBACK_URL = import.meta.env.VITE_API_FALLBACK_URL

export const axiosInstance = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
})

export const createInstance = <T>(
    config: AxiosRequestConfig & { sendWhileOffline?: boolean },
    options?: AxiosRequestConfig
): Promise<T> => {
    return axiosInstance({ ...config, ...options, signal: config.signal || options?.signal }).then((res) => res?.data)
}

export type BodyType<Data> = Data
export type ErrorType<Error> = AxiosError<Error>

// add access token to request headers
axiosInstance.interceptors.request.use((config) => {
    const { accessToken } = useAuthStore.getState()

    if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`
        config.headers['X-User-Shift-Id'] = useAuthStore.getState().xUserShiftId
    }

    if (!shouldTrackRequest(config) && isOffline()) {
        return Promise.reject(new axios.Cancel('GET requests are blocked'))
    }

    if (shouldTrackRequest(config) && isOffline()) {
        config.baseURL = FALLBACK_URL
    }

    return config
})

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const config = error.config
        const isFallbackURL = error.config.baseURL === FALLBACK_URL

        if (!config || config._retry) {
            return Promise.reject(error)
        }

        if (error.response?.status === 401) {
            msalInstance.clearCache()
            clearLocalStorageItem('auth-storage')
            window.location.href = ROUTES.LOGIN
            return
        }

        if (!shouldTrackRequest(config) || isOffline() || isUserError(error) || isFallbackURL || !FALLBACK_URL) {
            return Promise.reject(error)
        }

        try {
            const fallbackResponse = await axiosInstance({
                ...config,
                baseURL: FALLBACK_URL,
            })
            return fallbackResponse
        } catch (fallbackError) {
            return Promise.reject(fallbackError)
        }
    }
)

// axios instance for auth service

export const axiosAuthInstance = axios.create({
    baseURL: import.meta.env.VITE_API_AUTH_URL,
    withCredentials: true,
})

export const createAuthInstance = <T>(config: AxiosRequestConfig, options?: AxiosRequestConfig): Promise<T> => {
    return axiosAuthInstance({ ...config, ...options }).then((res) => res.data)
}

axiosAuthInstance.interceptors.request.use((config) => {
    const { accessToken } = useAuthStore.getState()

    if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
})

export const axiosHealthInstance = axios.create({
    baseURL: import.meta.env.VITE_API_AUTH_URL,
})

export const createHealthInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
    return axiosHealthInstance({ ...config }).then((res) => res.data)
}
